import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { Workspace } from './generated/schemas.ts';

// Puts a created or changed item into its cached list: in place of the one with its id, or at the end.
export const upsertInList = <Item extends { id: string }>(queryClient: QueryClient, queryKey: QueryKey, item: Item) =>
    queryClient.setQueryData<Item[]>(queryKey, (current) =>
        current?.some((existing) => existing.id === item.id)
            ? current.map((existing) => (existing.id === item.id ? item : existing))
            : [...(current ?? []), item]
    );

// Workspace-scoped queries live below ['workspaces', id], so the list is only invalidated with `exact`.
export const invalidateWorkspaces = (queryClient: QueryClient) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces'], exact: true });

// Written to the cache right away, so the new workspace's pages (or its new slug) are found before the refetch.
export const writeWorkspace = (queryClient: QueryClient, workspace: Workspace) => {
    upsertInList(queryClient, ['workspaces'], workspace);
    invalidateWorkspaces(queryClient);
};

// After deleting or leaving a workspace: it's gone from the list, and so is everything cached for it.
export const removeWorkspaceFromCache = (queryClient: QueryClient, id: string) => {
    queryClient.setQueryData<Workspace[]>(['workspaces'], (current) => current?.filter((item) => item.id !== id));
    invalidateWorkspaces(queryClient);
    queryClient.removeQueries({ queryKey: ['workspaces', id] });
};
